import { createContext, useContext, useEffect, useRef, useState } from "react";
import { doc, onSnapshot, updateDoc, setDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../firebase";
import { useAuth } from "./AuthContext";
import { sendEmail } from "../utils/email";

const TrialContext = createContext(null);

export function TrialProvider({ children }) {
  const { user } = useAuth();
  const [trialInfo, setTrialInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const healedRef = useRef(false);
  const emailSentRef = useRef(false);

  useEffect(() => {
    healedRef.current = false;
    emailSentRef.current = false;
    if (!user) {
      setTrialInfo(null);
      setLoading(false);
      return;
    }
    setLoading(true);

    // Live listener: when an admin extends a trial, a subscription changes
    // status, or an account is suspended/unsuspended, access updates right
    // away without the user having to log out and back in.
    const unsub = onSnapshot(
      doc(db, "users", user.uid),
      (snap) => {
        if (snap.exists()) {
          const data = snap.data();
          const subscription = data.subscription;
          const trialExpiresAt = data.trialExpiresAt ? new Date(data.trialExpiresAt) : null;
          const now = new Date();
          const isSubscribed =
            subscription?.status === "active" || subscription?.status === "trialing";
          const trialExpired = trialExpiresAt ? now > trialExpiresAt : false;
          const daysLeft = trialExpiresAt
            ? Math.max(0, Math.ceil((trialExpiresAt - now) / (1000 * 60 * 60 * 24)))
            : 0;

          setTrialInfo({
            isSubscribed,
            trialExpired,
            daysLeft,
            trialExpiresAt,
            hasAccess: isSubscribed || !trialExpired,
            suspended: !!data.suspended,
          });
          setLoading(false);

          // Fire-and-forget: this email must never block the app.
          if (false && !isSubscribed && daysLeft <= 1 && !trialExpired && !data.trialExpiryEmailSent && !emailSentRef.current) { // disabled: reminders are now sent by the daily server job
            emailSentRef.current = true;
            (async () => {
              try {
                await sendEmail("trial_expiring", user.email, {
                  name: data.fullName || "",
                  subscriptionUrl: `${window.location.origin}/subscription`,
                });
                await updateDoc(doc(db, "users", user.uid), { trialExpiryEmailSent: true });
              } catch (err) {
                console.error("Trial expiry email failed:", err);
              }
            })();
          }
        } else {
          // No Firestore profile for this authenticated user (signup interrupted).
          // Self-heal by creating a real 7-day trial record.
          const trialStartedAt = new Date().toISOString();
          const trialExpiresAtStr = new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString();
          if (!healedRef.current) {
            healedRef.current = true;
            setDoc(doc(db, "users", user.uid), {
              email: user.email,
              fullName: user.displayName || "",
              trialStartedAt,
              trialExpiresAt: trialExpiresAtStr,
              createdAt: serverTimestamp(),
            }, { merge: true }).catch((err) => {
              console.error("Failed to self-heal missing user profile:", err);
            });
          }
          setTrialInfo({
            isSubscribed: false,
            trialExpired: false,
            daysLeft: 7,
            trialExpiresAt: new Date(trialExpiresAtStr),
            hasAccess: true,
            suspended: false,
          });
          setLoading(false);
        }
      },
      (err) => {
        console.error("Trial listener error:", err);
        setLoading(false);
      }
    );
    return unsub;
  }, [user]);

  return (
    <TrialContext.Provider value={{ trialInfo, loading }}>
      {children}
    </TrialContext.Provider>
  );
}

export function useTrial() {
  const ctx = useContext(TrialContext);
  if (!ctx) throw new Error("useTrial must be used within TrialProvider");
  return ctx;
}